import { useDidMount } from 'hooks';
import { useEffect, useRef, useState } from 'react';
import {
  postCreateDeliverySession,
  postSetFallbackCartCharges
} from 'services/ApiCheckoutPageService';
import { subscribeUserEvents } from './util';
import { useDispatch } from 'react-redux';
import { withCache } from 'utils/withCache';
import { ThunkDispatch } from 'components/Shared/connect';
import { GtmAddShippingInfo } from 'store/googleTagManager/actions';

interface DeliveryWidgetProps {
  setShowFallback: (value: boolean) => void;
  disabled: boolean | undefined;
}

interface EmitAddShippingInfo {
  dispatch: ThunkDispatch;
}
const emitAddShippingInfo = ({ dispatch }: EmitAddShippingInfo) => {
  const addShippingInfoKey = 'added_shipping_info';
  const shouldEmitAddShippingInfo = withCache(cache => {
    const hasEmitted = cache.get(addShippingInfoKey);
    if (hasEmitted) return false;
    cache.set(addShippingInfoKey, true);
    return true;
  });

  if (!shouldEmitAddShippingInfo) return;
  dispatch(GtmAddShippingInfo());
};

export const DeliveryWidget = ({ setShowFallback, disabled }: DeliveryWidgetProps) => {
  const initialRender = useInitialRender();
  const container = useContainer();
  const widget = useWidget({ setShowFallback });
  const dispatch = useDispatch();

  if (!disabled) {
    emitAddShippingInfo({ dispatch });
  }

  useEffect(() => {
    const shouldRenderDeliveryWidget = !initialRender.isComplete() && widget.html;
    if (!shouldRenderDeliveryWidget) return;
    container.render(widget.html);
    initialRender.setComplete();
    subscribeUserEvents(dispatch);
  }, [widget.html, container, initialRender, dispatch]);

  return <div ref={container.ref} style={{ display: disabled ? 'none' : 'inline' }} />;
};

const useInitialRender = () => {
  const initialRenderRef = useRef(false);

  const setComplete = () => (initialRenderRef.current = true);
  const isComplete = () => initialRenderRef.current;

  return {
    setComplete,
    isComplete
  };
};

const useContainer = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const render = (widgetHtml: string) => {
    if (!containerRef.current) return;

    const content = document.createRange().createContextualFragment(widgetHtml);
    containerRef.current.appendChild(content);
  };

  return {
    render,
    ref: containerRef
  };
};

const useWidget = ({ setShowFallback }: Pick<DeliveryWidgetProps, 'setShowFallback'>) => {
  const status = useStatus();
  const [widgetHtml, setWidgetHtml] = useState<string>('');

  useDidMount(() => {
    if (widgetHtml) return;

    status.setLoading();

    getSession()
      .then(({ htmlSnippet }) => {
        setWidgetHtml(htmlSnippet);
        status.setComplete();
      })
      .catch(() => {
        setShowFallback(true);
        postSetFallbackCartCharges();
      });
  });

  return {
    html: widgetHtml,
    status
  };
};

const enum request {
  loading,
  complete,
  idle,
  error
}

const useStatus = () => {
  const [status, setStatus] = useState<request>(request.idle);

  const setIdle = () => setStatus(request.idle);
  const setLoading = () => setStatus(request.loading);
  const setError = () => setStatus(request.error);
  const setComplete = () => setStatus(request.complete);

  const isIdle = status === request.idle;
  const isLoading = status === request.loading;
  const isError = status === request.error;
  const isComplete = status === request.complete;

  return {
    setLoading,
    setIdle,
    setError,
    setComplete,

    isIdle,
    isLoading,
    isError,
    isComplete
  };
};

const getSession = async () => {
  return await postCreateDeliverySession();
};
